import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Paper, Grid, CircularProgress, Alert, Button, Divider, Rating, Chip 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { useParams, useNavigate } from 'react-router-dom';
import axiosServices from '../../api/interceptor';
import moment from 'moment';
import { useSelector } from 'react-redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ProbationEvaluationSingle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.tokens);
  
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const fetchEvaluation = async () => {
    try {
      setLoading(true);
      const response = await axiosServices.get(`/evaluations/${id}`);
      if (response.data && response.data.success) {
        setEvaluation(response.data.data);
      } else {
        setError('Evaluation not found');
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch evaluation details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvaluation();
  }, [id]);

  const isAdminOrHR = user?.permission === "Admin" || user?.permission === "Admin-a" || user?.permission === "HR Management";

  if (!user || !isAdminOrHR) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>You are not authorized to view this page. Only Admins and HR Management can access the list.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !evaluation) {
    return (
      <Container sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>
        <Alert severity="error">{error || 'Something went wrong'}</Alert>
      </Container>
    );
  }

  const getRecommendationColor = (rec) => {
    if (rec?.includes('Confirm employment')) return 'success';
    if (rec?.includes('Extend')) return 'warning';
    return 'error';
  };

  const exportPDF = async () => {
    try {
      setIsExporting(true);
      const input = document.getElementById('evaluation-single-content');
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = position - pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, pdfWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Probation_Evaluation_${evaluation.employeeName || 'Employee'}.pdf`);
    } catch (err) {
      console.error(err);
      alert('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const RatingRow = ({ label, data }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 0.5 }}>
        <Rating value={data?.score || 0} readOnly max={5} />
        <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
          {data?.score ? `${data.score} / 5` : 'Not Rated'}
        </Typography>
      </Box>
      <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
        <Typography variant="body2" sx={{ fontStyle: data?.comments ? 'normal' : 'italic', color: data?.comments ? 'text.primary' : 'text.secondary' }}>
          {data?.comments || 'No comments provided.'}
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/hr-management/probation-evaluation-list')}>
          Back to List
        </Button>
        <Button 
          variant="contained" 
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />} 
          onClick={exportPDF}
          disabled={isExporting}
        >
          {isExporting ? 'Generating...' : 'Download PDF'}
        </Button>
      </Box>
      
      <Paper id="evaluation-single-content" elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4 }}>
        <Box sx={{ borderBottom: '2px solid #1976d2', pb: 2, mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#203864' }}>
            Probation Period Evaluation Form
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Confidential - completed by {evaluation.evaluatorNameTitle}
          </Typography>
        </Box>

        {/* Section 1: Employee Details */}
        <Typography variant="h6" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 3, fontWeight: 'bold', color: '#334155' }}>
          Section 1: Employee Details
        </Typography>
        <Grid container spacing={3} sx={{ mb: 5 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Employee Name</Typography>
            <Typography variant="body1" fontWeight="500">{evaluation.employeeName}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Position / Role</Typography>
            <Typography variant="body1" fontWeight="500">{evaluation.positionRole}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Department / Team</Typography>
            <Typography variant="body1" fontWeight="500">{evaluation.departmentTeam}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Start Date</Typography>
            <Typography variant="body1" fontWeight="500">
              {evaluation.startDate ? moment(evaluation.startDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD MMM YYYY') : '--'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Probation End Date</Typography>
            <Typography variant="body1" fontWeight="500">
              {evaluation.probationEndDate ? moment(evaluation.probationEndDate, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD MMM YYYY') : '--'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Date of Evaluation</Typography>
            <Typography variant="body1" fontWeight="500">
              {evaluation.dateOfEvaluation ? moment(evaluation.dateOfEvaluation, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD MMM YYYY') : '--'}
            </Typography>
          </Grid>
        </Grid>

        {/* Section 2: Performance Evaluation */}
        <Typography variant="h6" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 3, fontWeight: 'bold', color: '#334155' }}>
          Section 2: Performance Evaluation
        </Typography>
        <Box sx={{ mb: 5 }}>
          <RatingRow label="1. Quality of Work" data={evaluation.ratings?.qualityOfWork} />
          <RatingRow label="2. Productivity and Deadlines" data={evaluation.ratings?.productivityAndDeadlines} />
          <RatingRow label="3. Skills and Job Knowledge" data={evaluation.ratings?.skillsAndJobKnowledge} />
          <RatingRow label="4. Learning and Adaptability" data={evaluation.ratings?.learningAndAdaptability} />
          <RatingRow label="5. Communication and Teamwork" data={evaluation.ratings?.communicationAndTeamwork} />
          <RatingRow label="6. Reliability and Initiative" data={evaluation.ratings?.reliabilityAndInitiative} />
        </Box>

        {/* Section 3: Overall Assessment */}
        <Typography variant="h6" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 3, fontWeight: 'bold', color: '#334155' }}>
          Section 3: Overall Assessment
        </Typography>
        <Box sx={{ mb: 5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Key Strengths:</Typography>
          <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography variant="body2">{evaluation.overallAssessment?.keyStrengths || 'None provided.'}</Typography>
          </Paper>

          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Areas for Improvement / Training Needed:</Typography>
          <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
            <Typography variant="body2">{evaluation.overallAssessment?.areasForImprovement || 'None provided.'}</Typography>
          </Paper>
        </Box>

        {/* Section 4: Probation Recommendation */}
        <Typography variant="h6" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 3, fontWeight: 'bold', color: '#334155' }}>
          Section 4: Recommendation
        </Typography>
        <Box sx={{ mb: 5, p: 3, border: '1px solid #e2e8f0', borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>Final Recommendation</Typography>
          <Chip 
            label={evaluation.probationRecommendation?.recommendation || 'No Recommendation'} 
            color={getRecommendationColor(evaluation.probationRecommendation?.recommendation)}
            sx={{ fontWeight: 'bold', fontSize: '1rem', py: 2.5, px: 1, mb: 3 }}
          />
          
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Justification / Comments:</Typography>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            {evaluation.probationRecommendation?.justification || 'No justification provided.'}
          </Typography>
        </Box>

        {/* Section 5: Sign-Off */}
        <Typography variant="h6" sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 3, fontWeight: 'bold', color: '#334155' }}>
          Section 5: Sign-Off
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Evaluator / Manager Name</Typography>
            <Typography variant="body1" fontWeight="500">{evaluation.evaluatorNameTitle}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Signature</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'cursive', color: '#0f172a' }}>
              {evaluation.signOff?.evaluatorSignature}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="textSecondary">Date</Typography>
            <Typography variant="body1" fontWeight="500">
              {evaluation.signOff?.date ? moment(evaluation.signOff.date, ['YYYY-MM-DD', 'DD/MM/YYYY']).format('DD MMM YYYY') : '--'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default ProbationEvaluationSingle;
